import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import AdminNotifications from "@/components/AdminNotifications";
import DeleteUserModal from "./admin/DeleteUserModal";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Crown, Users, Layers, Mail, Download, ShieldAlert, Check, X, Zap, Trash2 } from "lucide-react";

export default function AdminDashboard() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState({ users: 0, lead_magnets: 0, leads: 0, pro_users: 0 });
  const [users, setUsers] = useState([]);
  const [lms, setLms] = useState([]);
  const [flagged, setFlagged] = useState({ held: [], blocked_log: [] });
  const [tab, setTab] = useState("users");
  const [deleting, setDeleting] = useState(null); // user to confirm delete
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = async () => {
    try {
      const [s, u, l, f] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/lead-magnets"),
        api.get("/admin/referrals/flagged").catch(() => ({ data: { held: [], blocked_log: [] } })),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setLms(l.data.lead_magnets);
      setFlagged(f.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };
  useEffect(() => { load(); }, []);

  const moderate = async (refId, status) => {
    try {
      await api.patch(`/admin/referrals/${refId}`, { status });
      toast.success(status === "credited" ? "Approved & credited" : "Rejected");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      const r = await api.delete(`/admin/users/${deleting.user_id}`);
      const c = r.data.counts || {};
      const items = [
        c.lead_magnets && `${c.lead_magnets} lead magnet${c.lead_magnets === 1 ? "" : "s"}`,
        c.leads && `${c.leads} lead${c.leads === 1 ? "" : "s"}`,
        c.payment_transactions && `${c.payment_transactions} txn${c.payment_transactions === 1 ? "" : "s"}`,
      ].filter(Boolean).join(", ");
      toast.success(`${deleting.email} deleted${items ? ` · ${items}` : ""}`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const exportAll = () => {
    window.open(`${api.defaults.baseURL}/leads/export`, "_blank");
  };

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="label-cap mb-2 flex items-center gap-2"><Crown size={14} className="text-[#FF4F00]" /> Admin Console</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Platform Overview</h1>
        </div>
        <button onClick={exportAll} className="btn-brutal" data-testid="admin-export-btn"><Download size={16} /> Export All Leads CSV</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-black mb-8">
        <Stat icon={Users} label="Users" value={stats.users} testid="admin-stat-users" />
        <Stat icon={Crown} label="Pro Users" value={stats.pro_users} testid="admin-stat-pro" />
        <Stat icon={Layers} label="Lead Magnets" value={stats.lead_magnets} testid="admin-stat-lms" />
        <Stat icon={Mail} label="Leads" value={stats.leads} testid="admin-stat-leads" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setTab("users")} className={`px-4 py-2 border-2 border-black font-bold ${tab === "users" ? "bg-black text-white" : "bg-white"}`} data-testid="admin-tab-users">Users</button>
        <button onClick={() => setTab("lms")} className={`px-4 py-2 border-2 border-black font-bold ${tab === "lms" ? "bg-black text-white" : "bg-white"}`} data-testid="admin-tab-lms">All Lead Magnets</button>
        <button onClick={() => setTab("fraud")} className={`px-4 py-2 border-2 border-black font-bold flex items-center gap-2 ${tab === "fraud" ? "bg-black text-white" : "bg-white"}`} data-testid="admin-tab-fraud">
          <ShieldAlert size={14} /> Fraud Review
          {flagged.held.length > 0 && <span className="px-2 py-0.5 text-xs bg-[#FF4F00] text-white border border-black">{flagged.held.length}</span>}
        </button>
        <button onClick={() => setTab("notifications")} className={`px-4 py-2 border-2 border-black font-bold flex items-center gap-2 ${tab === "notifications" ? "bg-black text-white" : "bg-white"}`} data-testid="admin-tab-notifications">
          <Zap size={14} /> Notifications
        </button>
      </div>

      {tab === "users" && (
        <div className="border-2 border-black overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b-2 border-black">
              <tr>
                <th className="text-left p-3 font-display font-bold">Email</th>
                <th className="text-left p-3 font-display font-bold">Name</th>
                <th className="text-left p-3 font-display font-bold">Role</th>
                <th className="text-left p-3 font-display font-bold">Plan</th>
                <th className="text-left p-3 font-display font-bold">LMs</th>
                <th className="text-left p-3 font-display font-bold">Leads</th>
                <th className="text-left p-3 font-display font-bold">Joined</th>
                <th className="text-left p-3 font-display font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = me && u.user_id === me.user_id;
                const isAdmin = u.role === "admin";
                const canDelete = !isMe && !isAdmin;
                return (
                  <tr key={u.user_id} className="border-b border-zinc-200" data-testid={`admin-user-${u.user_id}`}>
                    <td className="p-3 font-mono text-xs">{u.email}</td>
                    <td className="p-3">{u.name}</td>
                    <td className="p-3"><span className={`px-2 py-1 text-xs font-bold border-2 border-black ${isAdmin ? "bg-[#FF4F00] text-white" : "bg-white"}`}>{u.role.toUpperCase()}</span></td>
                    <td className="p-3 font-bold">{u.plan}</td>
                    <td className="p-3 text-zinc-600">{u.lead_magnets_count ?? 0}</td>
                    <td className="p-3 text-zinc-600">{u.leads_count ?? 0}</td>
                    <td className="p-3 text-zinc-500 text-xs">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="p-3">
                      {canDelete ? (
                        <button
                          onClick={() => setDeleting(u)}
                          className="btn-brutal-outline !px-3 !py-1 text-xs inline-flex items-center gap-1 hover:bg-red-50 hover:border-red-800"
                          data-testid={`delete-user-${u.user_id}`}
                          title="Delete user + all their data"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      ) : (
                        <span className="text-xs text-zinc-400" title={isMe ? "You can't delete yourself" : "Admin — demote first"}>
                          {isMe ? "— you —" : "protected"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "lms" && (
        <div className="border-2 border-black overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b-2 border-black">
              <tr>
                <th className="text-left p-3 font-display font-bold">Title</th>
                <th className="text-left p-3 font-display font-bold">Slug</th>
                <th className="text-left p-3 font-display font-bold">Template</th>
                <th className="text-left p-3 font-display font-bold">Owner</th>
                <th className="text-left p-3 font-display font-bold">Published</th>
              </tr>
            </thead>
            <tbody>
              {lms.map((lm) => (
                <tr key={lm.id} className="border-b border-zinc-200" data-testid={`admin-lm-${lm.id}`}>
                  <td className="p-3 font-bold">{lm.title}</td>
                  <td className="p-3 font-mono text-xs">/{lm.slug}</td>
                  <td className="p-3">{lm.template_id}</td>
                  <td className="p-3 text-zinc-500 text-xs">{lm.user_id}</td>
                  <td className="p-3">{lm.published ? <span className="text-green-700 font-bold">YES</span> : <span className="text-zinc-400">No</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === "fraud" && (
        <div className="space-y-6">
          <div className="border-2 border-black">
            <div className="px-4 py-3 border-b-2 border-black bg-zinc-50 font-display font-bold text-lg flex items-center gap-2">
              <ShieldAlert size={16} className="text-[#FF4F00]" /> Held for review ({flagged.held.length})
            </div>
            {flagged.held.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No held referrals. All commissions auto-credited.</div>
            ) : (
              <div className="divide-y-2 divide-black">
                {flagged.held.map((r) => (
                  <div key={r.id} className="p-4 flex flex-wrap gap-3 items-center justify-between" data-testid={`held-${r.id}`}>
                    <div>
                      <div className="font-bold text-sm">{r.referred_email} <span className="text-zinc-500">→ ${r.commission}</span></div>
                      <div className="text-xs text-zinc-500 mt-1">Plan: {r.plan} · {(r.created_at || "").slice(0, 10)}</div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(r.fraud_reasons || []).map((reason) => (
                          <span key={reason} className="px-2 py-0.5 text-xs font-bold border border-black bg-yellow-100">{reason}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => moderate(r.id, "credited")} className="btn-brutal !px-3 !py-2 text-sm" data-testid={`approve-${r.id}`}><Check size={14} /> Approve</button>
                      <button onClick={() => moderate(r.id, "rejected")} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid={`reject-${r.id}`}><X size={14} /> Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-2 border-black">
            <div className="px-4 py-3 border-b-2 border-black bg-zinc-50 font-display font-bold text-lg">
              Blocked attempts ({flagged.blocked_log.length})
              <span className="ml-2 text-xs font-normal text-zinc-500">(self-referrals never created a commission row)</span>
            </div>
            {flagged.blocked_log.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No blocked attempts logged.</div>
            ) : (
              <div className="divide-y divide-zinc-200 max-h-[400px] overflow-y-auto">
                {flagged.blocked_log.slice(0, 50).map((b) => (
                  <div key={b.id} className="px-4 py-2 text-sm flex justify-between gap-3" data-testid={`blocked-${b.id}`}>
                    <span className="font-mono text-xs">{b.candidate_email}</span>
                    <span className="text-xs">
                      {(b.reasons || []).map((reason) => (
                        <span key={reason} className="ml-2 px-2 py-0.5 font-bold border border-black bg-red-100">{reason}</span>
                      ))}
                    </span>
                    <span className="text-xs text-zinc-400">{(b.created_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {tab === "notifications" && <AdminNotifications />}

      <DeleteUserModal
        user={deleting}
        loading={deleteLoading}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-6 border-r-2 last:border-r-0 border-black bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 label-cap mb-2"><Icon size={14} /> {label}</div>
      <div className="font-display font-black text-4xl">{value}</div>
    </div>
  );
}
